/**
 * Shared role definitions and resolution logic for dynamic RBAC.
 * Used by both Worker backend and React frontend.
 */

// ── Types ────────────────────────────────────────────────────────────────

export interface RoleDefinition {
  key: string; // lowercase alphanumeric + underscore, e.g. "captain"
  label: string; // display name, e.g. "Captain"
  priority: number; // higher = more privilege; admin=100, member=0
  isSystem: boolean; // true for "admin" and "member" — cannot be deleted
}

export interface AzureGroupMapping {
  roleKey: string;
  azureGroupId: string;
}

// ── Defaults ─────────────────────────────────────────────────────────────

export const DEFAULT_ROLES: RoleDefinition[] = [
  { key: 'admin', label: 'Admin', priority: 100, isSystem: true },
  { key: 'officer', label: 'Officer', priority: 50, isSystem: false },
  { key: 'member', label: 'Member', priority: 0, isSystem: true },
];

export const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  member: [
    'punch_self',
    'view_own_hours',
    'view_schedule',
    'view_roster',
    'view_on_shift',
    'request_swap',
    'request_coverage',
    'submit_feedback',
  ],
  officer: [
    'punch_self',
    'view_own_hours',
    'view_schedule',
    'view_roster',
    'view_on_shift',
    'request_swap',
    'request_coverage',
    'submit_feedback',
    'punch_others',
    'view_reports',
    'edit_entries',
    'void_entries',
    'propose_roster',
  ],
  admin: [
    'punch_self',
    'view_own_hours',
    'view_schedule',
    'view_roster',
    'view_on_shift',
    'request_swap',
    'request_coverage',
    'submit_feedback',
    'punch_others',
    'view_reports',
    'edit_entries',
    'void_entries',
    'propose_roster',
    'manage_members',
    'manage_positions',
    'manage_apparatus',
    'manage_categories',
    'manage_battalions',
    'manage_schedule_config',
    'manage_holidays',
    'sync_roster',
    'view_audit_log',
    'manage_azure_groups',
    'manage_rbac',
    'approve_roster',
    'triage_feedback',
  ],
};

// ── RBAC Permission Definitions ──────────────────────────────────────────

export interface RBACPermission {
  id: string;
  label: string;
  description: string;
  category: string;
}

export const RBAC_PERMISSIONS: RBACPermission[] = [
  {
    id: 'punch_self',
    label: 'Clock In/Out',
    description: 'Clock in and out for own shifts',
    category: 'Time Tracking',
  },
  {
    id: 'view_own_hours',
    label: 'View Own Hours',
    description: 'View personal time entries and summaries',
    category: 'Time Tracking',
  },
  {
    id: 'view_schedule',
    label: 'View Schedule',
    description: 'View the duty schedule calendar',
    category: 'Schedule',
  },
  { id: 'view_roster', label: 'View Roster', description: 'View battalion roster assignments', category: 'Schedule' },
  {
    id: 'view_on_shift',
    label: "View Who's On Shift",
    description: 'See who is currently on duty',
    category: 'Schedule',
  },
  {
    id: 'request_swap',
    label: 'Request Shift Swap',
    description: 'Create and manage shift swap requests',
    category: 'Schedule',
  },
  {
    id: 'request_coverage',
    label: 'Request Coverage',
    description: 'Request and respond to shift coverage',
    category: 'Schedule',
  },
  {
    id: 'submit_feedback',
    label: 'Submit Feedback',
    description: 'Submit feedback about the application',
    category: 'General',
  },
  {
    id: 'punch_others',
    label: 'Submit for Others',
    description: 'Clock in/out on behalf of other members',
    category: 'Officer',
  },
  {
    id: 'view_reports',
    label: 'View Reports',
    description: 'Access department-wide reports and analytics',
    category: 'Officer',
  },
  {
    id: 'edit_entries',
    label: 'Edit Entries',
    description: "Edit other members' time entries",
    category: 'Officer',
  },
  {
    id: 'void_entries',
    label: 'Void Entries',
    description: 'Void/delete time entries',
    category: 'Officer',
  },
  {
    id: 'propose_roster',
    label: 'Propose Roster Changes',
    description: 'Submit roster change proposals',
    category: 'Officer',
  },
  {
    id: 'manage_members',
    label: 'Manage Members',
    description: 'Add, edit, and manage member accounts',
    category: 'Admin',
  },
  {
    id: 'manage_positions',
    label: 'Manage Positions',
    description: 'Configure available positions',
    category: 'Admin',
  },
  {
    id: 'manage_apparatus',
    label: 'Manage Apparatus',
    description: 'Configure apparatus/units',
    category: 'Admin',
  },
  {
    id: 'manage_categories',
    label: 'Manage Categories',
    description: 'Configure time entry categories',
    category: 'Admin',
  },
  {
    id: 'manage_battalions',
    label: 'Manage Battalions',
    description: 'Configure battalions',
    category: 'Admin',
  },
  {
    id: 'manage_schedule_config',
    label: 'Manage Schedule Config',
    description: 'Configure shift schedule settings',
    category: 'Admin',
  },
  {
    id: 'manage_holidays',
    label: 'Manage Holidays',
    description: 'Manage holiday calendar',
    category: 'Admin',
  },
  {
    id: 'sync_roster',
    label: 'Sync Roster',
    description: 'Trigger roster sync from Airtable',
    category: 'Admin',
  },
  {
    id: 'view_audit_log',
    label: 'View Audit Log',
    description: 'Access system-wide audit trail',
    category: 'Admin',
  },
  {
    id: 'manage_azure_groups',
    label: 'Manage Azure AD Groups',
    description: 'Configure Azure AD group-to-role mappings',
    category: 'Admin',
  },
  {
    id: 'manage_rbac',
    label: 'Manage RBAC',
    description: 'Configure role-based access control settings',
    category: 'Admin',
  },
  {
    id: 'approve_roster',
    label: 'Approve Roster Changes',
    description: 'Approve or reject proposed roster changes',
    category: 'Admin',
  },
  {
    id: 'triage_feedback',
    label: 'Triage Feedback',
    description: 'Review and manage submitted feedback',
    category: 'Admin',
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────

/** Get the priority number for a role key, defaulting to 0 (member-level). */
export function getRolePriority(roleKey: string, roles?: RoleDefinition[]): number {
  const defs = roles ?? DEFAULT_ROLES;
  return defs.find((r) => r.key === roleKey)?.priority ?? 0;
}

/**
 * Resolve the highest-priority role for a user based on their Azure AD group
 * memberships and the configured group-to-role mappings.
 *
 * Mappings are checked in priority order (highest first). Returns the first
 * matching role key, or 'member' if no match.
 */
export function resolveRole(userGroups: string[], mappings: AzureGroupMapping[], roles?: RoleDefinition[]): string {
  const defs = roles ?? DEFAULT_ROLES;

  // Sort mappings by their role's priority (descending) so highest-priority match wins
  const sorted = [...mappings].sort((a, b) => {
    const priA = defs.find((r) => r.key === a.roleKey)?.priority ?? 0;
    const priB = defs.find((r) => r.key === b.roleKey)?.priority ?? 0;
    return priB - priA;
  });

  for (const mapping of sorted) {
    if (userGroups.includes(mapping.azureGroupId)) {
      return mapping.roleKey;
    }
  }

  return 'member';
}

/** Maximum number of roles allowed. */
export const MAX_ROLES = 10;

/** Validate a role key string (lowercase alphanumeric + underscore). */
export function isValidRoleKey(key: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(key) && key.length <= 30;
}
